/**
 * The messages between the ERP (the host page) and the fleet map it embeds in
 * an iframe from a separate origin (`apps/fleet-map`).
 *
 * The map runs on its own origin because Google's map script needs a looser
 * Content-Security-Policy than the ERP keeps. That makes the frame the less
 * trusted side, and the host the more trusted one: the frame never holds a
 * token, never calls the API, and receives only what it draws (plate, nickname,
 * position, heading, whether the van is moving, the time of its last message,
 * trail points; for a delivery stop its place, a short number and which run it
 * belongs to). The driver, the street address, the account and order numbers
 * and the speed stay in the ERP, which shows them in its own details card when
 * the frame reports a click.
 *
 * Every message is `{ v, type, payload }`. Both sides read **tolerantly**: a
 * message of an unknown version or type, or one that does not match its shape,
 * parses to `null` and is ignored, and fields a reader does not know are
 * dropped. The two sides deploy independently (and terminals keep old bundles
 * after a reload), so the protocol only grows by adding: a new message type, or
 * a new field a reader can do without. A change that is not additive needs
 * both versions supported for a release first.
 */
import { isFleetMessageStale } from "./fleet-tracking.js";
export const FLEET_MAP_PROTOCOL_VERSION = 1;
/** Largest numbers of items one message may carry; bigger ones are dropped. */
export const FLEET_MAP_MAX_VEHICLES = 1_000;
export const FLEET_MAP_MAX_STOPS = 1_000;
export const FLEET_MAP_MAX_TRAIL_POINTS = 50_000;
/** Longest accepted string, ids and labels included. */
export const FLEET_MAP_MAX_TEXT_LENGTH = 200;
export const FLEET_MAP_THEMES = ["light", "dark"];
/**
 * Where a delivery stop stands. A reader that meets a state it does not know
 * draws the stop as `pending`, so a newer host can add states.
 */
export const FLEET_MAP_STOP_STATES = ["pending", "next", "visited"];
/** Why the map could not start or lost its Google connection. */
export const FLEET_MAP_ERROR_CODES = [
    /** The build carries no Google Maps key. */
    "NOT_CONFIGURED",
    /** The Google script did not load. */
    "LOAD_FAILED",
    /** Google refused the key (referrer, quota, billing or API not enabled). */
    "AUTH_FAILED",
];
/**
 * The state of a drawn van at `now`: quiet for longer than the offline
 * threshold is OFFLINE whatever the last report said.
 */
export function fleetMapVehicleState(vehicle, now) {
    if (isFleetMessageStale(vehicle.messageAt, now)) {
        return "OFFLINE";
    }
    return vehicle.moving ? "MOVING" : "STOPPED";
}
/**
 * The colours of the runs on the map. Both sides use this list, so the swatch
 * beside a run in the ERP is the colour of its dots. None is close to the
 * green, grey and red of the vans.
 */
export const FLEET_MAP_RUN_COLORS = [
    "#1565c0",
    "#e65100",
    "#6a1b9a",
    "#00838f",
    "#c2185b",
    "#5d4037",
    "#3949ab",
    "#827717",
];
/** The colour of the stops of run number `group`; runs past the list reuse it. */
export function fleetMapRunColor(group) {
    return (FLEET_MAP_RUN_COLORS[group % FLEET_MAP_RUN_COLORS.length] ??
        FLEET_MAP_RUN_COLORS[0]);
}
/** Builds a host message of the current version. */
export function hostMessage(type, payload) {
    // The pairing of `type` and `payload` is fixed by the signature; TypeScript
    // cannot narrow the object literal against a generic discriminant.
    return {
        v: FLEET_MAP_PROTOCOL_VERSION,
        type,
        payload,
    };
}
/** Builds a frame message of the current version. */
export function frameMessage(type, payload) {
    return {
        v: FLEET_MAP_PROTOCOL_VERSION,
        type,
        payload,
    };
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function readText(value) {
    return typeof value === "string" && value.length <= FLEET_MAP_MAX_TEXT_LENGTH
        ? value
        : null;
}
function readNullableText(value) {
    if (value === null)
        return null;
    return readText(value) ?? undefined;
}
function readNonEmptyText(value) {
    const text = readText(value);
    return text !== null && text.length > 0 ? text : null;
}
function readFinite(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function readNullableNumber(value) {
    if (value === null)
        return null;
    return readFinite(value) ?? undefined;
}
function readLatitude(value) {
    const lat = readFinite(value);
    return lat !== null && Math.abs(lat) <= 90 ? lat : null;
}
function readLongitude(value) {
    const lng = readFinite(value);
    return lng !== null && Math.abs(lng) <= 180 ? lng : null;
}
/** An ISO date-time the browser can parse. */
function readTimestamp(value) {
    const text = readText(value);
    return text !== null && !Number.isNaN(Date.parse(text)) ? text : null;
}
function readOneOf(value, allowed) {
    return allowed.find((candidate) => candidate === value) ?? null;
}
function readVehicle(value) {
    if (!isRecord(value))
        return null;
    const telematicsVehicleId = readNonEmptyText(value["telematicsVehicleId"]);
    const title = readText(value["title"]);
    const subtitle = readNullableText(value["subtitle"]);
    const lat = readLatitude(value["lat"]);
    const lng = readLongitude(value["lng"]);
    const rawHeading = value["heading"];
    const heading = rawHeading === null ? null : readFinite(rawHeading);
    const rawMessageAt = value["messageAt"];
    const messageAt = rawMessageAt === null ? null : readTimestamp(rawMessageAt);
    if (telematicsVehicleId === null ||
        title === null ||
        subtitle === undefined ||
        lat === null ||
        lng === null ||
        (rawHeading !== null && heading === null) ||
        (rawMessageAt !== null && messageAt === null) ||
        typeof value["moving"] !== "boolean") {
        return null;
    }
    return {
        telematicsVehicleId,
        title,
        subtitle,
        lat,
        lng,
        heading,
        messageAt,
        moving: value["moving"],
    };
}
/** A whole number the frame can use as a colour index. */
function readGroup(value) {
    return typeof value === "number" &&
        Number.isInteger(value) &&
        value >= 0 &&
        value <= FLEET_MAP_MAX_STOPS * 1_000
        ? value
        : null;
}
function readStop(value) {
    if (!isRecord(value))
        return null;
    const id = readNonEmptyText(value["id"]);
    const lat = readLatitude(value["lat"]);
    const lng = readLongitude(value["lng"]);
    const label = readText(value["label"]);
    const group = readGroup(value["group"]);
    if (id === null ||
        lat === null ||
        lng === null ||
        label === null ||
        group === null) {
        return null;
    }
    return {
        id,
        lat,
        lng,
        label,
        state: readOneOf(value["state"], FLEET_MAP_STOP_STATES) ?? "pending",
        group,
    };
}
function readTrailPoint(value) {
    if (!isRecord(value))
        return null;
    const at = readTimestamp(value["at"]);
    const lat = readLatitude(value["lat"]);
    const lng = readLongitude(value["lng"]);
    return at === null || lat === null || lng === null ? null : { at, lat, lng };
}
/**
 * Every item must read, or the whole list does not: a trail with a point
 * missing draws a wrong line.
 */
function readList(value, max, readItem) {
    if (!Array.isArray(value) || value.length > max)
        return null;
    const items = [];
    const raws = value;
    for (const raw of raws) {
        const item = readItem(raw);
        if (item === null)
            return null;
        items.push(item);
    }
    return items;
}
/**
 * The vehicles that read, one per id (the later entry wins). An entry that
 * does not read is left out rather than discarding the update: the frame
 * redraws from each snapshot, so a van left out is gone from the map, where
 * a discarded update would leave every van at a stale position.
 */
function readVehicles(value) {
    if (!Array.isArray(value) || value.length > FLEET_MAP_MAX_VEHICLES) {
        return null;
    }
    const raws = value;
    const byId = new Map();
    for (const raw of raws) {
        const vehicle = readVehicle(raw);
        if (vehicle !== null)
            byId.set(vehicle.telematicsVehicleId, vehicle);
    }
    return [...byId.values()];
}
/**
 * The stops that read, one per id (the later entry wins). Like the vehicles,
 * an entry that does not read is left out rather than discarding the update.
 */
function readStops(value) {
    if (!Array.isArray(value) || value.length > FLEET_MAP_MAX_STOPS) {
        return null;
    }
    const raws = value;
    const byId = new Map();
    for (const raw of raws) {
        const stop = readStop(raw);
        if (stop !== null)
            byId.set(stop.id, stop);
    }
    return [...byId.values()];
}
function readLabels(value) {
    if (!isRecord(value) || !isRecord(value["vehicleState"]))
        return null;
    const state = value["vehicleState"];
    const moving = readText(state["MOVING"]);
    const stopped = readText(state["STOPPED"]);
    const offline = readText(state["OFFLINE"]);
    const stop = readNonEmptyText(value["stop"]);
    return moving === null || stopped === null || offline === null
        ? null
        : {
            vehicleState: { MOVING: moving, STOPPED: stopped, OFFLINE: offline },
            ...(stop !== null && { stop }),
        };
}
function readSelect(payload) {
    const id = payload["telematicsVehicleId"];
    if (id === null)
        return { telematicsVehicleId: null };
    const text = readNonEmptyText(id);
    return text === null ? null : { telematicsVehicleId: text };
}
function readStopSelect(payload) {
    const id = payload["stopId"];
    if (id === null)
        return { stopId: null };
    const text = readNonEmptyText(id);
    return text === null ? null : { stopId: text };
}
function readEnvelope(data) {
    if (!isRecord(data) || data["v"] !== FLEET_MAP_PROTOCOL_VERSION)
        return null;
    const type = data["type"];
    const payload = data["payload"];
    return typeof type === "string" && isRecord(payload)
        ? { type, payload }
        : null;
}
/**
 * Reads a message the host sent the frame. Null for anything that is not a
 * well-formed message of a type and version this reader knows.
 */
export function parseFleetMapHostMessage(data) {
    const envelope = readEnvelope(data);
    if (envelope === null)
        return null;
    const { type, payload } = envelope;
    switch (type) {
        case "init": {
            const theme = readOneOf(payload["theme"], FLEET_MAP_THEMES);
            const labels = readLabels(payload["labels"]);
            return theme === null || labels === null
                ? null
                : hostMessage("init", { theme, labels });
        }
        case "vehicles": {
            const vehicles = readVehicles(payload["vehicles"]);
            return vehicles === null ? null : hostMessage("vehicles", { vehicles });
        }
        case "trail": {
            const telematicsVehicleId = readNonEmptyText(payload["telematicsVehicleId"]);
            const points = readList(payload["points"], FLEET_MAP_MAX_TRAIL_POINTS, readTrailPoint);
            return telematicsVehicleId === null || points === null
                ? null
                : hostMessage("trail", { telematicsVehicleId, points });
        }
        case "select": {
            const select = readSelect(payload);
            return select === null ? null : hostMessage("select", select);
        }
        case "stops": {
            const stops = readStops(payload["stops"]);
            return stops === null ? null : hostMessage("stops", { stops });
        }
        case "selectStop": {
            const select = readStopSelect(payload);
            return select === null ? null : hostMessage("selectStop", select);
        }
        default:
            return null;
    }
}
/**
 * Reads a message the frame sent the host. The frame is the less trusted
 * side, so the host reads every field as untrusted text and numbers.
 */
export function parseFleetMapFrameMessage(data) {
    const envelope = readEnvelope(data);
    if (envelope === null)
        return null;
    const { type, payload } = envelope;
    switch (type) {
        case "ready":
            return frameMessage("ready", {});
        case "vehicleSelected": {
            const select = readSelect(payload);
            return select === null ? null : frameMessage("vehicleSelected", select);
        }
        case "stopSelected": {
            const select = readStopSelect(payload);
            return select === null ? null : frameMessage("stopSelected", select);
        }
        case "error": {
            const code = readOneOf(payload["code"], FLEET_MAP_ERROR_CODES);
            return code === null ? null : frameMessage("error", { code });
        }
        case "cspViolation": {
            const violatedDirective = readText(payload["violatedDirective"]);
            const effectiveDirective = readText(payload["effectiveDirective"]);
            const blockedUri = readText(payload["blockedUri"]);
            const documentUri = readText(payload["documentUri"]);
            const sourceFile = readNullableText(payload["sourceFile"]);
            const lineNumber = readNullableNumber(payload["lineNumber"]);
            const columnNumber = readNullableNumber(payload["columnNumber"]);
            const disposition = readOneOf(payload["disposition"], [
                "enforce",
                "report",
            ]);
            if (violatedDirective === null ||
                effectiveDirective === null ||
                blockedUri === null ||
                documentUri === null ||
                sourceFile === undefined ||
                lineNumber === undefined ||
                columnNumber === undefined ||
                disposition === null) {
                return null;
            }
            return frameMessage("cspViolation", {
                violatedDirective,
                effectiveDirective,
                blockedUri,
                documentUri,
                sourceFile,
                lineNumber,
                columnNumber,
                disposition,
            });
        }
        default:
            return null;
    }
}
